import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import './copilot.scss';

/**
 * LandOS Copilot — a read-only, role-scoped, citation-grounded side drawer.
 *
 * It mirrors the Help drawer's affordance (slides from the right, toggled from
 * the navbar) but instead of a static guide it answers in the user's own words,
 * grounded in the product's data. Everything it shows comes from the
 * `/api/copilot/ask` response:
 *   - `answer`      — plain text, never HTML, never executed;
 *   - `citations[]` — the tools that actually ran, as clickable source chips;
 *   - `navigations[]` — OFFERS to deep-link (via react-router), never forced.
 *
 * v1 is READ-ONLY and keeps NO history: the thread lives in this component for
 * the session only, and is sent back with each turn since the server persists
 * nothing.
 *
 * When the LLM section is off the API answers 400 with
 * `details.code === 'LLM_NOT_CONNECTED'`; the drawer renders that as an honest
 * "not connected" card linking an Admin to Settings — there is no fake mode.
 */

const SUGGESTIONS = [
  "What's blocked right now?",
  'Explain the freshness gate',
  // Deliberately place-free: a hardcoded locality ("Devanahalli") is one
  // market's vocabulary leaking into every other one — a Dubai user was being
  // offered a Bengaluru village. "Listings" is the app's own cross-market noun.
  'Which mandates match my active listings?',
];

/** A single source chip — clickable when it carries a route. */
function CitationChip({ citation, onNavigate }) {
  const clickable = Boolean(citation.route);
  return (
    <button
      type="button"
      className={`copilot-citation${clickable ? '' : ' copilot-citation-static'}`}
      onClick={clickable ? () => onNavigate(citation.route) : undefined}
      disabled={!clickable}
      title={clickable ? `Open ${citation.route}` : undefined}
    >
      <i className="fa fa-quote-left" aria-hidden="true" />
      <span>{citation.label}</span>
    </button>
  );
}

export default function CopilotDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const streamRef = useRef(null);
  const inputRef = useRef(null);

  // Keep the newest turn in view, and focus the input when the drawer opens.
  useEffect(() => {
    if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const goTo = (route) => {
    onClose();
    navigate(route);
  };

  const send = async (text) => {
    const question = text.trim();
    if (!question || loading) return;

    // The thread we replay to the server: only real user/assistant turns, never
    // the transient error/off cards.
    const priorThread = messages
      .filter((m) => !m.transient)
      .map((m) => ({ role: m.role, content: m.content }));

    const nextMessages = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await api.copilotAsk([...priorThread, { role: 'user', content: question }]);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.answer,
          citations: res.citations ?? [],
          navigations: res.navigations ?? [],
        },
      ]);
    } catch (err) {
      const notConnected = err.status === 400 && err.details?.code === 'LLM_NOT_CONNECTED';
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          transient: true,
          notConnected,
          content: err.message || 'The copilot could not answer that.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  return (
    <>
      <div
        className={`copilot-backdrop${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`copilot-drawer${open ? ' open' : ''}`}
        role="dialog"
        aria-label="LandOS Copilot"
        aria-hidden={!open}
      >
        <header className="copilot-head">
          <div className="copilot-title">
            <i className="fa fa-wand-magic-sparkles" aria-hidden="true" />
            <span>Ask LandOS</span>
          </div>
          <button
            type="button"
            className="copilot-close"
            onClick={onClose}
            aria-label="Close copilot"
          >
            <i className="fa fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <div className="copilot-stream" ref={streamRef}>
          {messages.length === 0 && (
            <div className="copilot-empty">
              <p className="copilot-empty-lead">
                Ask about your pipeline in plain words. I read only what you can already see, cite
                where each figure comes from, and can point you to the right screen — I never change
                anything.
              </p>
            </div>
          )}

          {messages.map((m, i) => {
            if (m.role === 'user') {
              return (
                <div key={i} className="copilot-msg copilot-msg-user">
                  <div className="copilot-bubble">{m.content}</div>
                </div>
              );
            }

            if (m.notConnected) {
              return (
                <div key={i} className="copilot-msg copilot-msg-assistant">
                  <div className="copilot-bubble copilot-bubble-off">
                    <p className="mb-2">
                      <i className="fa fa-plug-circle-xmark me-2" aria-hidden="true" />
                      {m.content}
                    </p>
                    <button
                      type="button"
                      className="copilot-nav-btn"
                      onClick={() => goTo('/admin/settings')}
                    >
                      <i className="fa fa-gear me-2" aria-hidden="true" />
                      Open Settings
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div key={i} className="copilot-msg copilot-msg-assistant">
                <div className={`copilot-bubble${m.transient ? ' copilot-bubble-error' : ''}`}>
                  {m.content}
                </div>

                {m.citations?.length > 0 && (
                  <div className="copilot-citations">
                    {m.citations.map((c, ci) => (
                      <CitationChip key={ci} citation={c} onNavigate={goTo} />
                    ))}
                  </div>
                )}

                {m.navigations?.length > 0 && (
                  <div className="copilot-navs">
                    {m.navigations.map((n, ni) => (
                      <button
                        key={ni}
                        type="button"
                        className="copilot-nav-btn"
                        onClick={() => goTo(n.route)}
                      >
                        <i className="fa fa-arrow-right-long me-2" aria-hidden="true" />
                        {n.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {loading && (
            <div className="copilot-msg copilot-msg-assistant">
              <div className="copilot-bubble copilot-thinking">
                <span className="copilot-dot" />
                <span className="copilot-dot" />
                <span className="copilot-dot" />
              </div>
            </div>
          )}
        </div>

        <form className="copilot-input" onSubmit={onSubmit}>
          {messages.length === 0 && (
            <div className="copilot-suggestions">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="copilot-chip"
                  onClick={() => send(s)}
                  disabled={loading}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="copilot-input-row">
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder="Ask about your pipeline…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              aria-label="Ask the copilot"
            />
            <button
              type="submit"
              className="btn btn-primary copilot-send"
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              <i className="fa fa-paper-plane" aria-hidden="true" />
            </button>
          </div>
          <p className="copilot-foot">Read-only · answers are grounded in your own records.</p>
        </form>
      </aside>
    </>
  );
}
